import { assertGradient } from "./gradient";
import type { SceneNode } from "../scene/model";
import { createNativeNode } from "./document";
export const interactiveRoles = [
  "quizButton",
  "inputField",
  "choiceOption",
  "dropZone",
  "dragItem",
] as const;
export type InteractiveRole = (typeof interactiveRoles)[number];
export const roleNames: Record<InteractiveRole, string> = {
  quizButton: "Кнопка",
  inputField: "Поле ввода",
  choiceOption: "Вариант ответа",
  dropZone: "Зона ответа",
  dragItem: "Перетаскиваемый элемент",
};
export const isInteractive = (n: SceneNode) =>
  (interactiveRoles as readonly unknown[]).includes(n.interaction.role);
export const animations = [
  "none",
  "wiggle-x",
  "wiggle-y",
  "spring",
  "tilt",
  "spin",
  "fade-out",
  "move-x",
  "shake",
];
export function createInteractive(
  id: string,
  role: InteractiveRole,
  x: number,
  y: number,
): SceneNode {
  const style = {
    quizButton: [140, 50, 8, "#3498db", "#1f5f8b", "#ffffff", 22],
    inputField: [320, 60, 10, "#ffffff", "#95a5a6", "#95a5a6", 20],
    choiceOption: [240, 60, 8, "#ffffff", "#95a5a6", "#2c3e50", 22],
    dropZone: [180, 120, 0, "#3498db", "#3498db", "#3b4b57", 18],
    dragItem: [180, 70, 8, "#fef3c7", "#d35400", "#7a3e00", 20],
  }[role]!;
  const n = createNativeNode(
    id,
    "rectangle",
    x,
    y,
    Number(style[0]),
    Number(style[1]),
  );
  n.geometry.cornerRadius = style[2];
  n.appearance = {
    ...n.appearance,
    fill: String(style[3]),
    stroke: String(style[4]),
    strokeEnabled: true,
    strokeWidth: 2,
    ...(role === "dropZone"
      ? { fillAlpha: 0.08, lineStyle: "dashed", strokeDash: [8, 6] }
      : {}),
  };
  n.layer.name = roleNames[role];
  n.interaction = {
    role,
    objectId: id,
    questionId: ["choiceOption", "dragItem"].includes(role) ? "q1" : "",
    displayText:
      role === "inputField"
        ? "Введите ответ"
        : role === "dropZone"
          ? "Перенесите в эту зону элемент"
          : roleNames[role],
    statsLabel: "",
    answerGroup: "",
    statsSaveMode: "answerAndCorrectness",
    correctValue: "",
    targetId: "",
    targetScreenId: "",
    scoreValue: role === "quizButton" ? 0 : 1,
    isCorrectOption: false,
    interactiveImageEnabled: false,
    hoverAnimation: "none",
    clickAnimation: "none",
    releaseAnimation: "none",
    visibilityConditionEnabled: false,
    visibilityConditionType: "answered",
    visibilityQuestionId: "",
    textColor: style[5],
    fontSize: style[6],
    fontFamily: "Arial",
  };
  return n;
}
export function assertInteraction(n: SceneNode, enabled: boolean) {
  const i = n.interaction;
  const fail = () => {
    throw new Error("Неподдержанные или повреждённые интерактивные свойства.");
  };
  if (!isInteractive(n)) {
    const expected =
      n.kind === "image"
        ? "decorImage"
        : n.kind === "text"
          ? "decorText"
          : "decorShape";
    if (
      i.role !== expected ||
      Object.keys(i).some(
        (k) =>
          k !== "role" &&
          !(enabled && n.kind === "text" && k === "dynamicTextType"),
      )
    )
      fail();
    if (
      i.dynamicTextType !== undefined &&
      !["none", "studentName", "lastResult", "totalResult"].includes(
        String(i.dynamicTextType),
      )
    )
      fail();
    return;
  }
  if (!enabled || n.kind !== "rectangle") fail();
  assertGradient(i.textGradient);
  if (
    i.textAlpha !== undefined &&
    (typeof i.textAlpha !== "number" ||
      !Number.isFinite(i.textAlpha) ||
      i.textAlpha < 0 ||
      i.textAlpha > 1)
  )
    fail();
  const stringKeys = [
    "objectId",
    "questionId",
    "displayText",
    "statsLabel",
    "answerGroup",
    "correctValue",
    "targetId",
    "targetScreenId",
    "visibilityQuestionId",
    "fontFamily",
    "textColor",
    "imageName",
    "hoverSoundName",
    "clickSoundName",
    "releaseSoundName",
  ];
  const booleanKeys = [
    "isCorrectOption",
    "interactiveImageEnabled",
    "visibilityConditionEnabled",
  ];
  const enums: Record<string, readonly string[]> = {
    statsSaveMode: [
      "answerAndCorrectness",
      "answerOnly",
      "correctnessOnly",
      "none",
    ],
    hoverAnimation: animations,
    clickAnimation: animations,
    releaseAnimation: animations,
    visibilityConditionType: [
      "answered",
      "correct",
      "allAnswered",
      "allCorrect",
    ],
  };
  const allowed = [
    "role",
    ...stringKeys,
    ...booleanKeys,
    ...Object.keys(enums),
    "scoreValue",
    "fontSize",
    "textGradient",
    "textAlpha",
  ];
  if (
    Object.keys(i).some((k) => !allowed.includes(k)) ||
    stringKeys.some(
      (k) =>
        i[k] !== undefined &&
        (typeof i[k] !== "string" || String(i[k]).length > 200000),
    ) ||
    booleanKeys.some((k) => i[k] !== undefined && typeof i[k] !== "boolean") ||
    Object.entries(enums).some(
      ([k, values]) => i[k] !== undefined && !values.includes(String(i[k])),
    ) ||
    !i.objectId ||
    !Number.isFinite(i.scoreValue) ||
    Number(i.scoreValue) < 0 ||
    !Number.isFinite(i.fontSize) ||
    Number(i.fontSize) < 1 ||
    Number(i.fontSize) > 1000 ||
    !/^#[\da-f]{6}$/i.test(String(i.textColor))
  )
    fail();
}
